import * as THREE from 'three';

/** Three.js material texture slots shown in the material panel. */
export const MATERIAL_MAP_DEFINITIONS = [
  { key: 'map', label: 'Map' },
  { key: 'specularMap', label: 'Specular Map' },
  { key: 'alphaMap', label: 'Alpha Map' },
  { key: 'envMap', label: 'Env Map' },
  { key: 'lightMap', label: 'Light Map' },
  { key: 'aoMap', label: 'AO Map' },
  { key: 'normalMap', label: 'Normal Map' },
  { key: 'roughnessMap', label: 'Roughness Map' },
  { key: 'metalnessMap', label: 'Metalness Map' },
  { key: 'emissiveMap', label: 'Emissive Map' },
  { key: 'bumpMap', label: 'Bump Map' },
  { key: 'displacementMap', label: 'Displacement Map' },
];

/** Color maps must stay in sRGB; data maps stay linear — matches GLTFLoader. */
const SRGB_MATERIAL_MAP_KEYS = new Set(['map', 'emissiveMap', 'specularMap']);

/**
 * Maps that MeshStandard/Physical can bind in WebGLMaterials without missing
 * uniforms. `specularMap` is Phong/Basic/Lambert only — assigning it on
 * Standard enables USE_SPECULARMAP but standard shaders omit
 * specularMapTransform → render crash (`.elements` / `.value` on undefined).
 */
const STANDARD_SAFE_MAP_KEYS = new Set([
  'map',
  'alphaMap',
  'envMap',
  'lightMap',
  'aoMap',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'emissiveMap',
  'bumpMap',
  'displacementMap',
]);

const PHONG_SAFE_MAP_KEYS = new Set([
  ...STANDARD_SAFE_MAP_KEYS,
  'specularMap',
]);

/** True when this map slot can be bound on the live Three.js material type. */
export function isMapSupportedOnMaterial(material, mapKey) {
  if (!material || !mapKey) return false;
  if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
    return STANDARD_SAFE_MAP_KEYS.has(mapKey);
  }
  if (
    material.isMeshPhongMaterial ||
    material.isMeshLambertMaterial ||
    material.isMeshBasicMaterial ||
    material.isMeshToonMaterial
  ) {
    return PHONG_SAFE_MAP_KEYS.has(mapKey);
  }
  // Unknown / custom: allow common slots only.
  return STANDARD_SAFE_MAP_KEYS.has(mapKey);
}

/**
 * Configure a freshly loaded texture so re-applying a downloaded map matches the
 * original GLB material (flipY + colorSpace + wrapping).
 */
export function configureMaterialMapTexture(texture, mapKey, referenceTexture = null) {
  if (!texture?.isTexture) return texture;

  // Heal textures that lost UV matrix (dispose / bad clone) — prevents
  // refreshTransformUniform → matrix.elements crashes on render.
  if (!texture.matrix?.elements) {
    texture.matrix = new THREE.Matrix3();
  }
  if (texture.matrixAutoUpdate === undefined) {
    texture.matrixAutoUpdate = true;
  }

  const ref = referenceTexture?.isTexture ? referenceTexture : null;

  texture.flipY =
    typeof ref?.flipY === 'boolean' ? ref.flipY : false;

  if (ref?.colorSpace) {
    texture.colorSpace = ref.colorSpace;
  } else if (SRGB_MATERIAL_MAP_KEYS.has(mapKey)) {
    texture.colorSpace = THREE.SRGBColorSpace;
  } else {
    texture.colorSpace = THREE.LinearSRGBColorSpace;
  }

  if (ref) {
    if (ref.wrapS != null) texture.wrapS = ref.wrapS;
    if (ref.wrapT != null) texture.wrapT = ref.wrapT;
    if (ref.magFilter != null) texture.magFilter = ref.magFilter;
    if (ref.minFilter != null) texture.minFilter = ref.minFilter;
    if (ref.repeat) texture.repeat.copy(ref.repeat);
    if (ref.offset) texture.offset.copy(ref.offset);
    if (ref.center) texture.center.copy(ref.center);
    if (typeof ref.rotation === 'number') texture.rotation = ref.rotation;
    if (typeof ref.anisotropy === 'number') texture.anisotropy = ref.anisotropy;
  }

  // Env maps need equirect/cube mapping — default UVMapping breaks sampling.
  if (mapKey === 'envMap' && texture.isTexture && !texture.isCubeTexture) {
    if (
      texture.mapping === THREE.UVMapping ||
      texture.mapping == null
    ) {
      texture.mapping = THREE.EquirectangularReflectionMapping;
    }
  }

  texture.needsUpdate = true;
  return texture;
}

/**
 * Detach shader-unsupported maps from a material (keep backups for the panel).
 * Call after bulk map edits / before render if needed.
 */
export function sanitizeMaterialMapsForShader(material) {
  if (!material) return false;
  let changed = false;
  const ud = ensureMapUserData(material);
  const backups = ud[MAP_BACKUP_USERDATA_KEY];
  const visibility = ud[MAP_VISIBILITY_USERDATA_KEY];

  MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
    const tex = material[key];
    if (!tex?.isTexture) return;
    if (isMapSupportedOnMaterial(material, key)) {
      if (!tex.matrix?.elements) {
        tex.matrix = new THREE.Matrix3();
        changed = true;
      }
      return;
    }
    // Unsupported on this material type — stash for UI, clear live slot.
    backups[key] = tex;
    if (visibility[key] === undefined) visibility[key] = true;
    material[key] = null;
    changed = true;
  });

  if (material.envMap && material.envMapRotation == null) {
    material.envMapRotation = new THREE.Euler();
    changed = true;
  }

  if (changed) material.needsUpdate = true;
  return changed;
}

/** Sanitize every material under a scene root. */
export function sanitizeObjectMapsForShader(root) {
  if (!root?.traverse) return;
  const visit = (mat) => {
    if (!mat) return;
    if (Array.isArray(mat)) mat.forEach(visit);
    else sanitizeMaterialMapsForShader(mat);
  };
  if (root.isMesh || root.isSkinnedMesh) visit(root.material);
  root.traverse?.((child) => {
    if (child.isMesh || child.isSkinnedMesh) visit(child.material);
  });
}

export const MAP_BACKUP_USERDATA_KEY = '_mapBackups';
export const MAP_VISIBILITY_USERDATA_KEY = '_mapVisibility';
export const MAP_PREVIEW_URLS_USERDATA_KEY = '_mapPreviewUrls';

/** Server/CDN URLs are safe to persist; blob/data URLs are editor-only previews. */
export function isPublishableTextureUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  const trimmed = url.trim();
  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) return false;
  return true;
}

/** Read drawable image from a Three texture (HTMLImage / canvas / video / bitmap). */
function getTextureImageSource(tex) {
  if (!tex?.isTexture) return null;
  return tex.image || tex.source?.data || null;
}

/**
 * GLTFExporter does not write Three.js `alphaMap`. Composite it into baseColor
 * alpha so transparency survives publish/reload in the GLB.
 * Returns true if baking happened.
 */
export function bakeAlphaMapIntoBaseColorTexture(material) {
  if (!material?.alphaMap?.isTexture) return false;

  const alphaSrc = getTextureImageSource(material.alphaMap);
  if (!alphaSrc) {
    material.transparent = true;
    if (material.depthWrite !== undefined) material.depthWrite = false;
    material.needsUpdate = true;
    return false;
  }

  const w = Math.max(
    1,
    alphaSrc.width || alphaSrc.videoWidth || alphaSrc.naturalWidth || 512
  );
  const h = Math.max(
    1,
    alphaSrc.height || alphaSrc.videoHeight || alphaSrc.naturalHeight || 512
  );

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;

  const baseSrc = getTextureImageSource(material.map);
  if (baseSrc) {
    try {
      ctx.drawImage(baseSrc, 0, 0, w, h);
    } catch {
      const hex = material.color?.isColor
        ? `#${material.color.getHexString()}`
        : '#ffffff';
      ctx.fillStyle = hex;
      ctx.fillRect(0, 0, w, h);
    }
  } else {
    const hex = material.color?.isColor
      ? `#${material.color.getHexString()}`
      : '#ffffff';
    ctx.fillStyle = hex;
    ctx.fillRect(0, 0, w, h);
  }

  let baseData;
  try {
    baseData = ctx.getImageData(0, 0, w, h);
  } catch {
    // Tainted canvas — keep transparent flag only.
    material.transparent = true;
    if (material.depthWrite !== undefined) material.depthWrite = false;
    material.needsUpdate = true;
    return false;
  }

  const aCanvas = document.createElement('canvas');
  aCanvas.width = w;
  aCanvas.height = h;
  const aCtx = aCanvas.getContext('2d', { willReadFrequently: true });
  try {
    aCtx.drawImage(alphaSrc, 0, 0, w, h);
  } catch {
    material.transparent = true;
    if (material.depthWrite !== undefined) material.depthWrite = false;
    material.needsUpdate = true;
    return false;
  }
  const alphaData = aCtx.getImageData(0, 0, w, h);

  // Three.js alphaMap samples the green channel for grayscale opacity.
  for (let i = 0; i < baseData.data.length; i += 4) {
    baseData.data[i + 3] = alphaData.data[i + 1];
  }
  ctx.putImageData(baseData, 0, 0);

  const baked = new THREE.CanvasTexture(canvas);
  baked.colorSpace = THREE.SRGBColorSpace;
  baked.flipY = false;
  baked.needsUpdate = true;
  const ref = material.map?.isTexture ? material.map : material.alphaMap;
  if (ref) {
    baked.wrapS = ref.wrapS;
    baked.wrapT = ref.wrapT;
    if (ref.repeat) baked.repeat.copy(ref.repeat);
    if (ref.offset) baked.offset.copy(ref.offset);
  }

  material.map = baked;
  if (material.color?.isColor) material.color.set(0xffffff);
  // Exporter cannot write alphaMap — leave opacity in map.a; reconstruct slot on load.
  material.alphaMap = null;
  material.transparent = true;
  if (material.depthWrite !== undefined) material.depthWrite = false;
  material.userData = material.userData || {};
  material.userData.editorAlphaBakedIntoMap = true;
  const ud = ensureMapUserData(material);
  ud[MAP_BACKUP_USERDATA_KEY].map = baked;
  ud[MAP_VISIBILITY_USERDATA_KEY].map = true;
  ud[MAP_VISIBILITY_USERDATA_KEY].alphaMap = true;
  delete ud[MAP_PREVIEW_URLS_USERDATA_KEY].alphaMap;
  material.needsUpdate = true;
  return true;
}

/**
 * After GLTF reload: if alpha was baked into baseColor, rebuild an alphaMap
 * slot from the map's alpha channel so the material panel reconstructs.
 */
export function restoreAlphaMapSlotFromBakedBaseColor(material) {
  if (!material?.map?.isTexture) return false;
  if (material.alphaMap?.isTexture) {
    // Ensure panel has a non-blob preview from the live texture.
    const ud = ensureMapUserData(material);
    ud[MAP_BACKUP_USERDATA_KEY].alphaMap = material.alphaMap;
    ud[MAP_VISIBILITY_USERDATA_KEY].alphaMap = true;
    const preview = getTexturePreviewUrl(material.alphaMap);
    if (preview) ud[MAP_PREVIEW_URLS_USERDATA_KEY].alphaMap = preview;
    material.transparent = true;
    if (material.depthWrite !== undefined) material.depthWrite = false;
    return true;
  }
  if (!material.userData?.editorAlphaBakedIntoMap && !material.transparent) {
    return false;
  }

  const src = getTextureImageSource(material.map);
  if (!src) return false;
  const w = Math.max(1, src.width || src.videoWidth || src.naturalWidth || 0);
  const h = Math.max(1, src.height || src.videoHeight || src.naturalHeight || 0);
  if (!w || !h) return false;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  try {
    ctx.drawImage(src, 0, 0, w, h);
  } catch {
    return false;
  }
  let data;
  try {
    data = ctx.getImageData(0, 0, w, h);
  } catch {
    return false;
  }

  // If alpha channel is fully opaque, nothing to reconstruct.
  let hasAlpha = false;
  for (let i = 3; i < data.data.length; i += 4) {
    if (data.data[i] < 250) {
      hasAlpha = true;
      break;
    }
  }
  if (!hasAlpha && !material.userData?.editorAlphaBakedIntoMap) return false;

  const gray = ctx.createImageData(w, h);
  for (let i = 0; i < data.data.length; i += 4) {
    const a = data.data[i + 3];
    gray.data[i] = a;
    gray.data[i + 1] = a;
    gray.data[i + 2] = a;
    gray.data[i + 3] = 255;
  }
  ctx.putImageData(gray, 0, 0);
  // redraw gray onto canvas for texture
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  out.getContext('2d').putImageData(gray, 0, 0);

  const alphaTex = new THREE.CanvasTexture(out);
  alphaTex.colorSpace = THREE.LinearSRGBColorSpace;
  alphaTex.flipY = false;
  alphaTex.needsUpdate = true;
  if (material.map) {
    alphaTex.wrapS = material.map.wrapS;
    alphaTex.wrapT = material.map.wrapT;
  }

  material.alphaMap = alphaTex;
  material.transparent = true;
  if (material.depthWrite !== undefined) material.depthWrite = false;
  const ud = ensureMapUserData(material);
  ud[MAP_BACKUP_USERDATA_KEY].alphaMap = alphaTex;
  ud[MAP_VISIBILITY_USERDATA_KEY].alphaMap = true;
  const preview = getTexturePreviewUrl(alphaTex);
  if (preview) ud[MAP_PREVIEW_URLS_USERDATA_KEY].alphaMap = preview;
  material.userData.editorAlphaBakedIntoMap = true;
  material.needsUpdate = true;
  return true;
}

export function restoreAlphaMapsInObject(root) {
  if (!root?.traverse) return;
  const visit = (mat) => {
    if (!mat) return;
    if (Array.isArray(mat)) mat.forEach(visit);
    else restoreAlphaMapSlotFromBakedBaseColor(mat);
  };
  if (root.isMesh || root.isSkinnedMesh) visit(root.material);
  root.traverse?.((child) => {
    if (child.isMesh || child.isSkinnedMesh) visit(child.material);
  });
}

/** Maps GLTFExporter writes into the binary (via core or bundled extensions). */
export const GLTF_NATIVE_MAP_KEYS = new Set([
  'map',
  'normalMap',
  'metalnessMap',
  'roughnessMap',
  'emissiveMap',
  'aoMap',
  'bumpMap',
]);

/**
 * Maps Three.js supports in the editor but GLTFExporter does not write.
 * These are embedded into material.userData.editorEmbeddedMaps for reconstruct.
 */
export const GLTF_USERDATA_MAP_KEYS = [
  'alphaMap',
  'specularMap',
  'lightMap',
  'envMap',
  'displacementMap',
];

export const EDITOR_EMBEDDED_MAPS_KEY = 'editorEmbeddedMaps';

/**
 * Map Intensity & Scale (+ AO intensity). glTF only round-trips a subset
 * (normalScale.x, ao strength, bumpFactor) and forces normalScale.y = -x on load.
 * Persist the full editor values in material extras for reconstruct.
 */
export const EDITOR_MAP_SCALARS_KEY = 'editorMapScalars';

const EDITOR_MAP_SCALAR_NUMBER_KEYS = [
  'bumpScale',
  'displacementScale',
  'displacementBias',
  'lightMapIntensity',
  'envMapIntensity',
  'aoMapIntensity',
];

/** Snapshot current Map Intensity & Scale values for GLB extras. */
export function captureEditorMapScalars(material) {
  if (!material) return null;
  const out = {};

  if (material.normalScale?.isVector2) {
    out.normalScale = {
      x: material.normalScale.x,
      y: material.normalScale.y,
    };
  }

  EDITOR_MAP_SCALAR_NUMBER_KEYS.forEach((key) => {
    if (typeof material[key] === 'number' && Number.isFinite(material[key])) {
      out[key] = material[key];
    }
  });

  return Object.keys(out).length ? out : null;
}

/** Apply scalars captured in material.userData.editorMapScalars. */
export function restoreEditorMapScalarsFromUserData(material) {
  if (!material) return false;
  const scalars = material.userData?.[EDITOR_MAP_SCALARS_KEY];
  if (!scalars || typeof scalars !== 'object') return false;

  let changed = false;

  if (
    scalars.normalScale &&
    typeof scalars.normalScale.x === 'number' &&
    typeof scalars.normalScale.y === 'number' &&
    material.normalScale?.isVector2
  ) {
    material.normalScale.set(scalars.normalScale.x, scalars.normalScale.y);
    changed = true;
  }

  EDITOR_MAP_SCALAR_NUMBER_KEYS.forEach((key) => {
    if (
      typeof scalars[key] === 'number' &&
      Number.isFinite(scalars[key]) &&
      material[key] !== undefined
    ) {
      material[key] = scalars[key];
      changed = true;
    }
  });

  if (changed) material.needsUpdate = true;
  return changed;
}

/** Stash Map Intensity & Scale onto material.userData before GLTF export. */
export function prepareEditorMapScalarsForGltfExport(material) {
  if (!material) return false;
  const captured = captureEditorMapScalars(material);
  if (!captured) return false;
  material.userData = material.userData || {};
  material.userData[EDITOR_MAP_SCALARS_KEY] = captured;
  return true;
}

const MAX_EMBED_MAP_EDGE = 1024;

/** Encode a live texture to a PNG/JPEG data URL for GLB extras / API fallback. */
export function textureToDataUrl(tex, maxEdge = MAX_EMBED_MAP_EDGE) {
  const src = getTextureImageSource(tex);
  if (!src) return null;
  const sw = src.width || src.videoWidth || src.naturalWidth || 0;
  const sh = src.height || src.videoHeight || src.naturalHeight || 0;
  if (!sw || !sh) return null;

  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  const w = Math.max(1, Math.round(sw * scale));
  const h = Math.max(1, Math.round(sh * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    ctx.drawImage(src, 0, 0, w, h);
  } catch {
    return null;
  }
  try {
    // Prefer PNG so alpha survives for alphaMap / cutouts.
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * Before GLTF export: stash editor maps into userData (extras), then bake alpha
 * into baseColor so glTF transparency survives. Embed happens first so alphaMap
 * (and blob-only uploads) are still available to encode.
 */
export function prepareEditorMapsForGltfExport(material) {
  if (!material) return false;
  let changed = false;

  const embedded = {
    ...(material.userData?.[EDITOR_EMBEDDED_MAPS_KEY] || {}),
  };
  const previews = material.userData?.[MAP_PREVIEW_URLS_USERDATA_KEY] || {};
  const backups = material.userData?.[MAP_BACKUP_USERDATA_KEY] || {};

  // Embed every map the exporter may drop, plus any user-assigned slot (preview
  // URL) as a reconstruct safety net for blob/local uploads.
  MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
    const mustEmbed =
      GLTF_USERDATA_MAP_KEYS.includes(key) || !!previews[key];
    if (!mustEmbed) return;
    const tex =
      (material[key]?.isTexture ? material[key] : null) ||
      (backups[key]?.isTexture ? backups[key] : null);
    if (!tex) return;
    const dataUrl = textureToDataUrl(tex);
    if (!dataUrl) return;
    embedded[key] = dataUrl;
    changed = true;
  });

  // Bake alpha into baseColor AFTER embedding so the panel can restore alphaMap.
  if (material.alphaMap?.isTexture) {
    const ud = ensureMapUserData(material);
    if (!ud[MAP_BACKUP_USERDATA_KEY].alphaMap) {
      ud[MAP_BACKUP_USERDATA_KEY].alphaMap = material.alphaMap;
    }
    if (bakeAlphaMapIntoBaseColorTexture(material)) changed = true;
  }

  // Fallback if bake ran before embed could capture alphaMap.
  if (
    material.userData?.editorAlphaBakedIntoMap &&
    material.map?.isTexture &&
    !embedded.alphaMap
  ) {
    const gray = extractAlphaChannelDataUrl(material.map);
    if (gray) {
      embedded.alphaMap = gray;
      changed = true;
    }
  }

  material.userData = material.userData || {};
  if (Object.keys(embedded).length) {
    material.userData[EDITOR_EMBEDDED_MAPS_KEY] = embedded;
  }

  // Persist Map Intensity & Scale — glTF drops most of these on round-trip.
  if (prepareEditorMapScalarsForGltfExport(material)) {
    changed = true;
  }

  // Drop ephemeral blob preview URLs from extras (broken thumbs after reload).
  if (material.userData[MAP_PREVIEW_URLS_USERDATA_KEY]) {
    const cleaned = {};
    Object.entries(material.userData[MAP_PREVIEW_URLS_USERDATA_KEY]).forEach(
      ([k, url]) => {
        if (isPublishableTextureUrl(url)) cleaned[k] = url;
      }
    );
    if (Object.keys(cleaned).length) {
      material.userData[MAP_PREVIEW_URLS_USERDATA_KEY] = cleaned;
    } else {
      delete material.userData[MAP_PREVIEW_URLS_USERDATA_KEY];
    }
  }

  return changed;
}

function extractAlphaChannelDataUrl(tex) {
  const src = getTextureImageSource(tex);
  if (!src) return null;
  const w = src.width || src.videoWidth || src.naturalWidth || 0;
  const h = src.height || src.videoHeight || src.naturalHeight || 0;
  if (!w || !h) return null;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  try {
    ctx.drawImage(src, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h);
    const gray = ctx.createImageData(w, h);
    for (let i = 0; i < data.data.length; i += 4) {
      const a = data.data[i + 3];
      gray.data[i] = a;
      gray.data[i + 1] = a;
      gray.data[i + 2] = a;
      gray.data[i + 3] = 255;
    }
    ctx.putImageData(gray, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * After GLTF load: restore map slots that were embedded in material extras.
 * Returns a Promise that resolves when all TextureLoader jobs finish.
 */
export function restoreEmbeddedMapsFromMaterialUserData(material) {
  if (!material) return Promise.resolve(false);
  const embedded = material.userData?.[EDITOR_EMBEDDED_MAPS_KEY];
  if (!embedded || typeof embedded !== 'object') return Promise.resolve(false);

  const loader = new THREE.TextureLoader();
  const tasks = [];

  Object.entries(embedded).forEach(([key, url]) => {
    if (!url || typeof url !== 'string') return;

    // Slot already filled by GLTFLoader — keep it, sync panel metadata.
    // Exception: alphaMap must come back even when only baseColor was written.
    if (material[key]?.isTexture && key !== 'alphaMap') {
      const ud = ensureMapUserData(material);
      ud[MAP_BACKUP_USERDATA_KEY][key] = material[key];
      ud[MAP_VISIBILITY_USERDATA_KEY][key] = true;
      if (!ud[MAP_PREVIEW_URLS_USERDATA_KEY][key]) {
        const preview = getTexturePreviewUrl(material[key]);
        if (preview) ud[MAP_PREVIEW_URLS_USERDATA_KEY][key] = preview;
        else if (url.startsWith('data:')) {
          ud[MAP_PREVIEW_URLS_USERDATA_KEY][key] = url;
        }
      }
      return;
    }

    tasks.push(
      new Promise((resolve) => {
        loader.load(
          url,
          (texture) => {
            configureMaterialMapTexture(texture, key, material[key] || null);
            const ud = ensureMapUserData(material);
            ud[MAP_BACKUP_USERDATA_KEY][key] = texture;
            ud[MAP_VISIBILITY_USERDATA_KEY][key] = true;
            ud[MAP_PREVIEW_URLS_USERDATA_KEY][key] = url;
            if (isMapSupportedOnMaterial(material, key)) {
              material[key] = texture;
            }
            if (key === 'alphaMap') {
              material.transparent = true;
              if (material.depthWrite !== undefined) material.depthWrite = false;
            }
            material.needsUpdate = true;
            try {
              window.dispatchEvent(
                new CustomEvent('materialMapsRestored', {
                  detail: { materialUuid: material.uuid, mapKey: key },
                })
              );
            } catch {
              /* ignore */
            }
            resolve(true);
          },
          undefined,
          () => resolve(false)
        );
      })
    );
  });

  if (!tasks.length) return Promise.resolve(false);
  return Promise.all(tasks).then((results) => results.some(Boolean));
}

/** Restore all editor maps on an asset after GLB reconstruct. */
export function restoreAllEditorMapsAfterGltfLoad(root, settings = null) {
  if (!root) return Promise.resolve();

  const embedTasks = [];

  const visit = (mat) => {
    if (!mat) return;
    if (Array.isArray(mat)) {
      mat.forEach(visit);
      return;
    }
    embedTasks.push(
      restoreEmbeddedMapsFromMaterialUserData(mat).then(() => {
        restoreAlphaMapSlotFromBakedBaseColor(mat);
        restoreEditorMapScalarsFromUserData(mat);
      })
    );
  };

  if (root.isMesh || root.isSkinnedMesh) visit(root.material);
  root.traverse?.((child) => {
    if (child.isMesh || child.isSkinnedMesh) visit(child.material);
  });

  const tasks = [...embedTasks];
  if (settings?.maps || settings?.mapPreviewUrls) {
    tasks.push(restoreMaterialMapsFromSettings(root, settings));
  }

  return Promise.all(tasks).then(() => {
    restoreAlphaMapsInObject(root);
    try {
      window.dispatchEvent(
        new CustomEvent('materialMapsRestored', {
          detail: { root: true },
        })
      );
    } catch {
      /* ignore */
    }
  });
}

/** True when the user assigned map textures in the editor (forces GLB re-bake). */
export function hasEditorAssignedMapsInHierarchy(root) {
  if (!root?.traverse) return false;
  let found = false;
  root.traverse((obj) => {
    if ((!obj.isMesh && !obj.isSkinnedMesh) || !obj.material) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    mats.forEach((mat) => {
      if (!mat) return;
      const previews = mat.userData?.[MAP_PREVIEW_URLS_USERDATA_KEY];
      if (previews && Object.values(previews).some(Boolean)) found = true;
      if (mat.userData?.[EDITOR_EMBEDDED_MAPS_KEY]) found = true;
      if (mat.userData?.editorAlphaBakedIntoMap) found = true;
      MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
        if (mat[key]?.isTexture && previews?.[key]) found = true;
      });
      // Any non-native map currently bound means editor work that must re-bake.
      GLTF_USERDATA_MAP_KEYS.forEach((key) => {
        if (mat[key]?.isTexture) found = true;
        const backup = mat.userData?.[MAP_BACKUP_USERDATA_KEY]?.[key];
        if (backup?.isTexture) found = true;
      });
    });
  });
  return found;
}

export function materialHasMapTexture(mat, mapKey) {
  if (!mat || !mapKey) return false;
  const backup = mat.userData?.[MAP_BACKUP_USERDATA_KEY]?.[mapKey];
  return !!(mat[mapKey]?.isTexture || backup?.isTexture);
}

export function objectHasEmbeddedGltfMaps(object) {
  return getMeshesFromObject(object).some((mesh) => {
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    return materials.some((mat) => mat?.map?.isTexture);
  });
}

export function getMeshesFromObject(object) {
  if (!object) return [];
  const meshes = [];
  if (object.isMesh) meshes.push(object);
  object.traverse?.((child) => {
    if (child.isMesh) meshes.push(child);
  });
  return meshes;
}

function extensionFromMime(mime) {
  if (!mime) return 'png';
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
  if (mime.includes('webp')) return 'webp';
  if (mime.includes('gif')) return 'gif';
  return 'png';
}

function extensionFromUrl(url) {
  if (typeof url !== 'string') return null;
  const clean = url.split('?')[0].split('#')[0];
  const match = clean.match(/\.(png|jpe?g|webp|gif)$/i);
  return match ? match[1].toLowerCase().replace('jpeg', 'jpg') : null;
}

async function fetchUrlAsBlob(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch texture: ${response.status}`);
  return response.blob();
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode texture'))),
      'image/png'
    );
  });
}

/**
 * Export the object's texture at native resolution / original file quality.
 * Prefers the original image bytes when available; otherwise encodes full-size PNG.
 */
export async function getFullQualityTextureBlob(texture) {
  if (!texture) return null;

  const img = texture.image || texture.source?.data || null;
  if (!img) return null;

  const tryUrl = async (url) => {
    if (!url || typeof url !== 'string') return null;
    // data: URLs that are already full uploads — keep original bytes
    if (url.startsWith('data:') || url.startsWith('blob:') || /^https?:/i.test(url)) {
      try {
        const blob = await fetchUrlAsBlob(url);
        return {
          blob,
          extension: extensionFromMime(blob.type) || extensionFromUrl(url) || 'png',
        };
      } catch {
        return null;
      }
    }
    return null;
  };

  if (typeof img === 'string') {
    const fromString = await tryUrl(img);
    if (fromString) return fromString;
  }

  const src = img.src || img.currentSrc || null;
  if (src) {
    const fromSrc = await tryUrl(src);
    if (fromSrc) return fromSrc;
  }

  const width = img.naturalWidth || img.width || 0;
  const height = img.naturalHeight || img.height || 0;
  if (!width || !height) return null;

  const canDraw =
    (typeof HTMLCanvasElement !== 'undefined' &&
      (img instanceof HTMLCanvasElement ||
        img instanceof HTMLImageElement ||
        (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) ||
        (typeof OffscreenCanvas !== 'undefined' &&
          img instanceof OffscreenCanvas))) ||
    (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap);

  if (!canDraw && !(img.width && img.height)) return null;

  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { colorSpace: 'srgb' }) || canvas.getContext('2d');
    if (!ctx) return null;
    // Keep pixel orientation identical to the GPU image (GLTF uses flipY=false).
    // Do not vertically flip here — re-apply configures flipY to match the original.
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await canvasToPngBlob(canvas);
    return { blob, extension: 'png' };
  } catch {
    return null;
  }
}

export async function downloadFullQualityMaterialMap(texture, label, fallbackUrl) {
  const safeName = String(label || 'material-map')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'material-map';

  let result = await getFullQualityTextureBlob(texture);

  if (!result && fallbackUrl) {
    try {
      const blob = await fetchUrlAsBlob(fallbackUrl);
      result = {
        blob,
        extension: extensionFromMime(blob.type) || extensionFromUrl(fallbackUrl) || 'png',
      };
    } catch {
      result = null;
    }
  }

  if (!result?.blob) return false;

  const objectUrl = URL.createObjectURL(result.blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${safeName}.${result.extension || 'png'}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
  return true;
}

/** Preview URL for UI thumbnails (data URL, http URL, or blob). */
export function getTexturePreviewUrl(texture) {
  if (!texture) return null;

  const img = texture.image || texture.source?.data;
  if (!img) return null;

  if (typeof img === 'string') return img;
  if (img.src) return img.src;
  if (img.currentSrc) return img.currentSrc;

  // Avoid synchronous full-res PNG encode — that hangs publish / UI on large bump maps.
  const MAX_PREVIEW_EDGE = 256;

  if (typeof HTMLCanvasElement !== 'undefined' && img instanceof HTMLCanvasElement) {
    try {
      if (img.width <= MAX_PREVIEW_EDGE && img.height <= MAX_PREVIEW_EDGE) {
        return img.toDataURL('image/jpeg', 0.7);
      }
      const scale =
        MAX_PREVIEW_EDGE / Math.max(img.width || 1, img.height || 1);
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      return canvas.toDataURL('image/jpeg', 0.7);
    } catch {
      return null;
    }
  }

  if (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) {
    try {
      const scale =
        MAX_PREVIEW_EDGE / Math.max(img.width || 1, img.height || 1);
      const w = Math.max(1, Math.round((img.width || 1) * Math.min(1, scale)));
      const h = Math.max(1, Math.round((img.height || 1) * Math.min(1, scale)));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      return canvas.toDataURL('image/jpeg', 0.7);
    } catch {
      return null;
    }
  }

  return null;
}

export function ensureMapUserData(mat) {
  if (!mat) return null;
  mat.userData = mat.userData || {};
  if (!mat.userData[MAP_BACKUP_USERDATA_KEY]) {
    mat.userData[MAP_BACKUP_USERDATA_KEY] = {};
  }
  if (!mat.userData[MAP_VISIBILITY_USERDATA_KEY]) {
    mat.userData[MAP_VISIBILITY_USERDATA_KEY] = {};
  }
  if (!mat.userData[MAP_PREVIEW_URLS_USERDATA_KEY]) {
    mat.userData[MAP_PREVIEW_URLS_USERDATA_KEY] = {};
  }
  return mat.userData;
}

export function setMapPreviewUrl(mat, mapKey, url) {
  if (!mat || !mapKey || !url) return;
  const ud = ensureMapUserData(mat);
  ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = url;
}

/**
 * Prefer a durable URL for undo/redo (stored preview / image src).
 * Avoids relying only on compressed thumbnail data URLs.
 */
export function getMaterialMapUndoUrl(material, mapKey) {
  if (!material || !mapKey) return null;
  const stored =
    material.userData?.[MAP_PREVIEW_URLS_USERDATA_KEY]?.[mapKey] || null;
  if (typeof stored === 'string' && stored.trim()) return stored;

  const resolved = resolveMapOnMaterial(material, mapKey);
  if (typeof resolved.previewUrl === 'string' && resolved.previewUrl.trim()) {
    return resolved.previewUrl;
  }

  const tex = resolved.texture;
  const img = tex?.image || tex?.source?.data;
  if (!img) return null;
  if (typeof img === 'string') return img;
  if (img.src) return img.src;
  if (img.currentSrc) return img.currentSrc;
  return null;
}

/** Snapshot one map slot on a material for undo/redo. */
export function captureMaterialMapSlotState(material, mapKey) {
  if (!material || !mapKey) return null;
  const resolved = resolveMapOnMaterial(material, mapKey);
  const url = getMaterialMapUndoUrl(material, mapKey);
  if (url) setMapPreviewUrl(material, mapKey, url);
  return {
    materialUuid: material.uuid,
    mapKey,
    url,
    visibility: !!resolved.enabled,
    hasTexture: !!(resolved.texture || url),
  };
}

/** Snapshot a map slot across every material under an object. */
export function captureObjectMapSlotStates(object, mapKey) {
  const byUuid = new Map();
  getMeshesFromObject(object).forEach((mesh) => {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((mat) => {
      if (!mat?.uuid || byUuid.has(mat.uuid)) return;
      byUuid.set(mat.uuid, {
        material: mat,
        state: captureMaterialMapSlotState(mat, mapKey),
      });
    });
  });
  return Array.from(byUuid.values());
}

/** Restore one map slot from an undo snapshot. */
export function applyMaterialMapSlotState(material, mapKey, state) {
  if (!material || !mapKey || !state) return Promise.resolve();

  const ud = ensureMapUserData(material);

  if (!state.hasTexture || !state.url) {
    const old = material[mapKey] || ud[MAP_BACKUP_USERDATA_KEY][mapKey];
    if (old?.isTexture) {
      ud[MAP_BACKUP_USERDATA_KEY][mapKey] = old;
    }
    ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = false;
    material[mapKey] = null;
    delete ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey];
    material.needsUpdate = true;
    return Promise.resolve();
  }

  setMapPreviewUrl(material, mapKey, state.url);

  return new Promise((resolve) => {
    const loader = new THREE.TextureLoader();
    loader.load(
      state.url,
      (texture) => {
        const old = material[mapKey] || ud[MAP_BACKUP_USERDATA_KEY][mapKey];
        configureMaterialMapTexture(texture, mapKey, old);
        ud[MAP_BACKUP_USERDATA_KEY][mapKey] = texture;
        ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = state.visibility !== false;
        if (
          state.visibility !== false &&
          isMapSupportedOnMaterial(material, mapKey)
        ) {
          material[mapKey] = texture;
          if (mapKey === 'envMap' && material.envMapRotation == null) {
            material.envMapRotation = new THREE.Euler();
          }
          if (mapKey === 'alphaMap') {
            material.transparent = true;
            if (material.depthWrite !== undefined) material.depthWrite = false;
          }
        } else {
          material[mapKey] = null;
        }
        material.needsUpdate = true;
        resolve();
      },
      undefined,
      () => resolve()
    );
  });
}

export function applyObjectMapSlotStates(entries, mapKey) {
  if (!entries?.length || !mapKey) return Promise.resolve();
  return Promise.all(
    entries.map(({ material, state }) =>
      applyMaterialMapSlotState(material, mapKey, state)
    )
  );
}

const MATERIAL_LIBRARY_UNDO_KEY = 'materialLibrary';

/** Capture mesh ↔ material bindings + unused library for list undo. */
export function captureMeshMaterialBindings(root) {
  if (!root) return { bindings: [], library: [], materialNames: {} };
  const bindings = [];
  const materialNames = {};

  const visit = (mesh) => {
    if (!mesh?.isMesh && !mesh?.isSkinnedMesh) return;
    const mats = Array.isArray(mesh.material)
      ? [...mesh.material]
      : [mesh.material];
    bindings.push({
      mesh,
      materials: mats.filter(Boolean),
    });
    mats.forEach((mat) => {
      if (mat?.uuid) materialNames[mat.uuid] = mat.name || '';
    });
  };

  if (root.isMesh || root.isSkinnedMesh) visit(root);
  root.traverse?.(visit);

  const library = Array.isArray(root.userData?.[MATERIAL_LIBRARY_UNDO_KEY])
    ? [...root.userData[MATERIAL_LIBRARY_UNDO_KEY]]
    : [];
  library.forEach((mat) => {
    if (mat?.uuid) materialNames[mat.uuid] = mat.name || '';
  });

  return {
    bindings,
    library,
    materialNames,
    libraryKey: MATERIAL_LIBRARY_UNDO_KEY,
  };
}

export function restoreMeshMaterialBindings(root, snapshot) {
  if (!root || !snapshot) return;
  snapshot.bindings?.forEach(({ mesh, materials }) => {
    if (!mesh) return;
    const list = (materials || []).filter(Boolean);
    mesh.material = list.length > 1 ? list : list[0] || mesh.material;
    mesh.userData = mesh.userData || {};
    mesh.userData.threeMaterialEdited = true;
  });

  const libraryKey = snapshot.libraryKey || MATERIAL_LIBRARY_UNDO_KEY;
  root.userData = root.userData || {};
  root.userData[libraryKey] = Array.isArray(snapshot.library)
    ? [...snapshot.library]
    : [];
  root.userData.threeMaterialEdited = true;

  if (snapshot.materialNames) {
    const applyName = (mat) => {
      if (!mat?.uuid) return;
      if (snapshot.materialNames[mat.uuid] !== undefined) {
        mat.name = snapshot.materialNames[mat.uuid];
      }
    };
    snapshot.bindings?.forEach(({ materials }) => materials?.forEach(applyName));
    snapshot.library?.forEach(applyName);
  }
}

export function getMapVisibility(mat, mapKey) {
  if (!mat) return false;
  const explicit = mat.userData?.[MAP_VISIBILITY_USERDATA_KEY]?.[mapKey];
  if (typeof explicit === 'boolean') return explicit;
  return !!mat[mapKey];
}

export function resolveMapOnMaterial(mat, mapKey) {
  if (!mat) return { texture: null, enabled: false };

  const backups = mat.userData?.[MAP_BACKUP_USERDATA_KEY] || {};
  const previewUrls = mat.userData?.[MAP_PREVIEW_URLS_USERDATA_KEY] || {};
  const texture = mat[mapKey] || backups[mapKey] || null;
  const enabled = texture ? getMapVisibility(mat, mapKey) : false;

  return {
    texture,
    enabled,
    previewUrl: previewUrls[mapKey] || (texture ? getTexturePreviewUrl(texture) : null),
  };
}

/**
 * Collect map state from the selected object (first mesh with each map wins).
 */
export function collectMaterialMapList(object) {
  const meshes = getMeshesFromObject(object);
  const sampleMat = (() => {
    for (const mesh of meshes) {
      const materials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];
      const mat = materials.find(Boolean);
      if (mat) return mat;
    }
    return null;
  })();

  return MATERIAL_MAP_DEFINITIONS.map(({ key, label }) => {
    let texture = null;
    let enabled = false;
    let previewUrl = null;
    let aoMapIntensity = 1;
    const supported = sampleMat
      ? isMapSupportedOnMaterial(sampleMat, key)
      : key !== 'specularMap';

    for (const mesh of meshes) {
      const materials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];

      for (const mat of materials) {
        if (!mat) continue;
        const resolved = resolveMapOnMaterial(mat, key);
        if (resolved.texture || resolved.previewUrl) {
          texture = resolved.texture;
          enabled = resolved.enabled;
          previewUrl = resolved.previewUrl;
          if (key === 'aoMap' && mat.aoMapIntensity != null) {
            aoMapIntensity = mat.aoMapIntensity;
          }
          break;
        }
      }
      if (texture || previewUrl) break;
    }

    return {
      key,
      label,
      texture,
      previewUrl,
      hasTexture: !!(texture || previewUrl),
      enabled: !!(texture || previewUrl) && enabled,
      aoMapIntensity,
      supported,
    };
  });
}

/** Toggle map on mesh (texture stays in backup; boolean controls slot binding). */
export function setMaterialMapVisibility(object, mapKey, visible) {
  const meshes = getMeshesFromObject(object);

  meshes.forEach((mesh) => {
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];

    materials.forEach((mat) => {
      if (!mat) return;

      const ud = ensureMapUserData(mat);
      const backups = ud[MAP_BACKUP_USERDATA_KEY];
      const texture = mat[mapKey] || backups[mapKey];
      if (!texture) return;

      backups[mapKey] = texture;
      const preview = getTexturePreviewUrl(texture);
      if (preview) {
        ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = preview;
      }

      ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = visible;
      mat[mapKey] =
        visible && isMapSupportedOnMaterial(mat, mapKey) ? texture : null;
      if (visible && mapKey === 'envMap' && mat.envMapRotation == null) {
        mat.envMapRotation = new THREE.Euler();
      }
      mat.needsUpdate = true;
    });
  });
}

/** True when the same texture instance is bound to another map slot or backup. */
export function isTextureReferencedOnMaterial(material, texture, excludeMapKey) {
  if (!material || !texture?.isTexture) return false;

  for (const { key } of MATERIAL_MAP_DEFINITIONS) {
    if (key === excludeMapKey) continue;
    if (material[key] === texture) return true;
    const backup = material.userData?.[MAP_BACKUP_USERDATA_KEY]?.[key];
    if (backup === texture) return true;
  }

  return false;
}

/** Populate map backups / preview URLs from live material slots (after GLB load). */
export function syncMaterialMapBackupsFromSlots(object) {
  getMeshesFromObject(object).forEach((mesh) => {
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];

    materials.forEach((mat) => {
      if (!mat) return;
      const ud = ensureMapUserData(mat);

      MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
        const texture = mat[key];
        if (!texture?.isTexture) return;

        ud[MAP_BACKUP_USERDATA_KEY][key] = texture;
        if (typeof ud[MAP_VISIBILITY_USERDATA_KEY][key] !== 'boolean') {
          ud[MAP_VISIBILITY_USERDATA_KEY][key] = true;
        }
        const preview = getTexturePreviewUrl(texture);
        if (preview) {
          ud[MAP_PREVIEW_URLS_USERDATA_KEY][key] = preview;
        }
      });
    });
  });
}

/**
 * Restore map textures from stored settings (maps / mapPreviewUrls / legacy fields).
 * Skips URL loads when the slot already has a texture from GLB.
 */
export function restoreMaterialMapsFromSettings(object, settings = {}) {
  if (!object || !settings || typeof settings !== 'object') {
    return Promise.resolve();
  }

  const maps = settings.maps || {};
  const mapPreviewUrls = settings.mapPreviewUrls || {};
  const mapVisibility = settings.mapVisibility || {};
  const urlsByKey = new Map();

  MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
    const url = maps[key] || mapPreviewUrls[key];
    if (isPublishableTextureUrl(url)) {
      urlsByKey.set(key, url);
    }
  });

  if (isPublishableTextureUrl(settings.textureImage)) {
    urlsByKey.set('emissiveMap', settings.textureImage);
  }
  if (isPublishableTextureUrl(settings.NormalMap)) {
    urlsByKey.set('normalMap', settings.NormalMap);
  }
  if (isPublishableTextureUrl(settings.metallic?.map)) {
    urlsByKey.set('metalnessMap', settings.metallic.map);
  }

  if (!urlsByKey.size) {
    return Promise.resolve();
  }

  const loader = new THREE.TextureLoader();
  if (typeof loader.setCrossOrigin === 'function') {
    loader.setCrossOrigin('anonymous');
  }

  const meshes = getMeshesFromObject(object);
  const tasks = [];

  urlsByKey.forEach((url, mapKey) => {
    if (!isPublishableTextureUrl(url)) return;

    tasks.push(
      new Promise((resolve) => {
        const syncVisibilityOnExisting = () => {
          meshes.forEach((mesh) => {
            const materials = Array.isArray(mesh.material)
              ? mesh.material
              : [mesh.material];
            materials.forEach((mat) => {
              if (!mat || !materialHasMapTexture(mat, mapKey)) return;
              const ud = ensureMapUserData(mat);
              const texture =
                mat[mapKey] || ud[MAP_BACKUP_USERDATA_KEY][mapKey];
              if (!texture?.isTexture) return;
              ud[MAP_BACKUP_USERDATA_KEY][mapKey] = texture;
              if (isPublishableTextureUrl(url)) {
                ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = url;
              }
              if (typeof mapVisibility[mapKey] === 'boolean') {
                ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = mapVisibility[mapKey];
                mat[mapKey] = mapVisibility[mapKey] ? texture : null;
              }
              mat.needsUpdate = true;
            });
          });
        };

        const materialsNeedingUrl = [];
        meshes.forEach((mesh) => {
          const materials = Array.isArray(mesh.material)
            ? mesh.material
            : [mesh.material];
          materials.forEach((mat) => {
            if (!mat || materialHasMapTexture(mat, mapKey)) return;
            materialsNeedingUrl.push(mat);
          });
        });

        if (!materialsNeedingUrl.length) {
          syncVisibilityOnExisting();
          resolve();
          return;
        }

        loader.load(
          url,
          (texture) => {
            if (!texture?.isTexture) {
              resolve();
              return;
            }
            texture.needsUpdate = true;
            const visible =
              typeof mapVisibility[mapKey] === 'boolean'
                ? mapVisibility[mapKey]
                : true;

            materialsNeedingUrl.forEach((mat) => {
              const ud = ensureMapUserData(mat);
              const old =
                mat[mapKey] || ud[MAP_BACKUP_USERDATA_KEY][mapKey] || null;
              configureMaterialMapTexture(texture, mapKey, old);
              ud[MAP_BACKUP_USERDATA_KEY][mapKey] = texture;
              ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = url;
              ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = visible;
              mat[mapKey] = visible ? texture : null;
              mat.needsUpdate = true;
            });
            syncVisibilityOnExisting();
            resolve();
          },
          undefined,
          () => resolve()
        );
      })
    );
  });

  return tasks.length ? Promise.all(tasks) : Promise.resolve();
}

export function syncMapVisibilitySettings(object) {
  const meshes = getMeshesFromObject(object);
  const mapVisibility = {};

  MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
    let visible = false;
    let hasMap = false;

    for (const mesh of meshes) {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        const { texture, enabled } = resolveMapOnMaterial(mat, key);
        if (texture || mat?.userData?.[MAP_PREVIEW_URLS_USERDATA_KEY]?.[key]) {
          hasMap = true;
          visible = enabled;
          break;
        }
      }
      if (hasMap) break;
    }

    if (hasMap) {
      mapVisibility[key] = visible;
    }
  });

  return mapVisibility;
}

/** Snapshot visibility + preview URLs for GLB export extras (not API). */
export function collectMapExportExtrasFromObject(object) {
  const meshes = getMeshesFromObject(object);
  const mapVisibility = {};
  const mapPreviewUrls = {};

  for (const { key } of MATERIAL_MAP_DEFINITIONS) {
    let done = false;
    for (const mesh of meshes) {
      if (done) break;
      const materials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];

      for (const mat of materials) {
        if (!mat) continue;
        const resolved = resolveMapOnMaterial(mat, key);
        const preview =
          resolved.previewUrl ||
          mat.userData?.[MAP_PREVIEW_URLS_USERDATA_KEY]?.[key];

        if (!resolved.texture && !preview) continue;

        mapVisibility[key] = getMapVisibility(mat, key);
        if (preview) mapPreviewUrls[key] = preview;
        done = true;
        break;
      }
    }
  }

  return { mapVisibility, mapPreviewUrls };
}

function meshSlotKey(mesh, root = null) {
  const parts = [];
  let node = mesh;
  while (node && node !== root) {
    parts.unshift(node.name || node.uuid);
    node = node.parent;
  }
  return parts.join('/');
}

function resolveMeshHierarchyRoot(object) {
  if (!object) return object;
  const isMeshWrapper = object.deleteType === 'mesh' || object.objType === '3d';
  if (
    isMeshWrapper &&
    Array.isArray(object.children) &&
    object.children.length === 1 &&
    object.children[0]?.traverse
  ) {
    return object.children[0];
  }
  return object;
}

function resolveMeshSlot(mesh, root, meshByKey, meshByName) {
  const relativeKey = meshSlotKey(mesh, root);
  if (relativeKey && meshByKey.has(relativeKey)) {
    return meshByKey.get(relativeKey);
  }
  const simpleName = mesh.name || mesh.uuid;
  if (simpleName && meshByName.has(simpleName)) {
    return meshByName.get(simpleName);
  }
  return null;
}

/** Copy map backups, visibility, preview URLs, and slot bindings between two materials. */
export function copyMaterialMapUserData(sourceMat, targetMat) {
  if (!sourceMat || !targetMat) return;

  const srcUd = ensureMapUserData(sourceMat);
  const tgtUd = ensureMapUserData(targetMat);

  tgtUd[MAP_VISIBILITY_USERDATA_KEY] = {
    ...(srcUd[MAP_VISIBILITY_USERDATA_KEY] || {}),
  };
  tgtUd[MAP_PREVIEW_URLS_USERDATA_KEY] = {
    ...(srcUd[MAP_PREVIEW_URLS_USERDATA_KEY] || {}),
  };
  tgtUd[MAP_BACKUP_USERDATA_KEY] = {};

  MATERIAL_MAP_DEFINITIONS.forEach(({ key }) => {
    const srcBackups = srcUd[MAP_BACKUP_USERDATA_KEY] || {};
    const texture = srcBackups[key] || sourceMat[key] || null;
    if (!texture) return;

    tgtUd[MAP_BACKUP_USERDATA_KEY][key] = texture;
    const visible =
      typeof srcUd[MAP_VISIBILITY_USERDATA_KEY]?.[key] === 'boolean'
        ? srcUd[MAP_VISIBILITY_USERDATA_KEY][key]
        : !!sourceMat[key];
    tgtUd[MAP_VISIBILITY_USERDATA_KEY][key] = visible;
    targetMat[key] = visible ? texture : null;
  });

  if (sourceMat.userData?.originalMaterialProps) {
    targetMat.userData.originalMaterialProps = {
      ...sourceMat.userData.originalMaterialProps,
    };
  }
  if (sourceMat.userData?.[EDITOR_EMBEDDED_MAPS_KEY]) {
    targetMat.userData[EDITOR_EMBEDDED_MAPS_KEY] = {
      ...sourceMat.userData[EDITOR_EMBEDDED_MAPS_KEY],
    };
  }
  if (sourceMat.userData?.[EDITOR_MAP_SCALARS_KEY]) {
    targetMat.userData[EDITOR_MAP_SCALARS_KEY] = {
      ...sourceMat.userData[EDITOR_MAP_SCALARS_KEY],
    };
  }
  if (sourceMat.userData?.editorAlphaBakedIntoMap) {
    targetMat.userData.editorAlphaBakedIntoMap = true;
  }

  targetMat.needsUpdate = true;
}

function copyMeshMaterialMaps(sourceMesh, targetMesh) {
  if (!sourceMesh?.material || !targetMesh?.material) return;

  const sourceMats = Array.isArray(sourceMesh.material)
    ? sourceMesh.material
    : [sourceMesh.material];
  const targetMats = Array.isArray(targetMesh.material)
    ? targetMesh.material
    : [targetMesh.material];

  sourceMats.forEach((srcMat, index) => {
    const tgtMat = targetMats[index] ?? targetMats[0];
    if (srcMat && tgtMat) {
      copyMaterialMapUserData(srcMat, tgtMat);
    }
  });

  if (sourceMesh.userData?.threeMaterialEdited) {
    targetMesh.userData = targetMesh.userData || {};
    targetMesh.userData.threeMaterialEdited = true;
  }
}

/** Copy per-mesh texture map state from source object onto duplicate target. */
export function copyMaterialMapsBetweenObjects(sourceObject, targetObject) {
  if (!sourceObject || !targetObject) return;

  const sourceRoot = resolveMeshHierarchyRoot(sourceObject);
  const targetRoot = resolveMeshHierarchyRoot(targetObject);
  const sourceMeshes = getMeshesFromObject(sourceRoot);
  const targetMeshes = getMeshesFromObject(targetRoot);
  if (!sourceMeshes.length || !targetMeshes.length) return;

  if (sourceMeshes.length === 1 && targetMeshes.length === 1) {
    copyMeshMaterialMaps(sourceMeshes[0], targetMeshes[0]);
    return;
  }

  const sourceByKey = new Map();
  const sourceByName = new Map();
  sourceMeshes.forEach((mesh) => {
    const relativeKey = meshSlotKey(mesh, sourceRoot);
    if (relativeKey) sourceByKey.set(relativeKey, mesh);
    const simpleName = mesh.name || mesh.uuid;
    if (simpleName && !sourceByName.has(simpleName)) {
      sourceByName.set(simpleName, mesh);
    }
  });

  targetMeshes.forEach((targetMesh) => {
    const sourceMesh =
      resolveMeshSlot(targetMesh, targetRoot, sourceByKey, sourceByName) ||
      (sourceMeshes.length === 1 ? sourceMeshes[0] : null);
    if (sourceMesh) {
      copyMeshMaterialMaps(sourceMesh, targetMesh);
    }
  });
}

/** After GLB load: restore hidden map images from material extras (not API). */
export function restoreMapsFromGltfUserData(object) {
  const meshes = getMeshesFromObject(object);
  const loader = new THREE.TextureLoader();
  if (typeof loader.setCrossOrigin === 'function') {
    loader.setCrossOrigin('anonymous');
  }

  const tasks = [];

  meshes.forEach((mesh) => {
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];

    materials.forEach((mat) => {
      if (!mat?.userData) return;

      const mapVisibility = mat.userData[MAP_VISIBILITY_USERDATA_KEY];
      const mapPreviewUrls = mat.userData[MAP_PREVIEW_URLS_USERDATA_KEY];
      if (!mapPreviewUrls || typeof mapPreviewUrls !== 'object') return;

      ensureMapUserData(mat);

      Object.entries(mapPreviewUrls).forEach(([mapKey, url]) => {
        if (!isPublishableTextureUrl(url)) return;
        if (materialHasMapTexture(mat, mapKey)) return;

        const visible =
          mapVisibility && typeof mapVisibility[mapKey] === 'boolean'
            ? mapVisibility[mapKey]
            : true;

        tasks.push(
          new Promise((resolve) => {
            loader.load(
              url,
              (texture) => {
                if (!texture?.isTexture) {
                  resolve();
                  return;
                }
                if (materialHasMapTexture(mat, mapKey)) {
                  resolve();
                  return;
                }
                const ud = ensureMapUserData(mat);
                const old =
                  mat[mapKey] || ud[MAP_BACKUP_USERDATA_KEY][mapKey] || null;
                configureMaterialMapTexture(texture, mapKey, old);
                ud[MAP_BACKUP_USERDATA_KEY][mapKey] = texture;
                ud[MAP_PREVIEW_URLS_USERDATA_KEY][mapKey] = url;
                ud[MAP_VISIBILITY_USERDATA_KEY][mapKey] = visible;
                mat[mapKey] = visible ? texture : null;
                mat.needsUpdate = true;
                resolve();
              },
              undefined,
              () => resolve()
            );
          })
        );
      });
    });
  });

  return tasks.length ? Promise.all(tasks) : Promise.resolve();
}
